import React, { useEffect, useState, useRef } from 'react';
import { PlusIcon, TrashIcon, FileTextIcon, SaveIcon, DownloadIcon, MailIcon, InfoIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { FileUpload } from '../FileUpload';
import { OrgUnitSelector } from '../settings/orgunit/OrgUnitSelector';
import { flattenOrgUnits, sampleOrgUnits, type OrgUnit } from '../financial/sharedFinancialData';
export function RCTIEntry({
  rcti = null,
  onSave,
  onCancel,
  onGeneratePDF,
  onSendEmail,
  locale = 'en-US'
}) {
  const [formData, setFormData] = useState({
    rctiNumber: rcti?.rctiNumber || 'AUTO-GENERATED',
    orgUnitId: rcti?.orgUnitId || '',
    orgUnitName: rcti?.orgUnitName || '',
    orgUnitPath: rcti?.orgUnitPath || '',
    vendor: rcti?.vendor?.id || '',
    rctiDate: rcti?.rctiDate || new Date().toISOString().split('T')[0],
    currency: rcti?.currency || 'AUD',
    taxScheme: rcti?.taxScheme || 'GST',
    taxIdentifier: rcti?.taxIdentifier || '',
    reference: rcti?.reference || '',
    notes: rcti?.notes || '',
    declarationText: rcti?.declarationText || 'This is a Recipient Created Tax Invoice issued under the agreed arrangement with the vendor. No tax invoice should be issued by the vendor for these items.'
  });
  const [lineItems, setLineItems] = useState(rcti?.lineItems || [{
    id: 1,
    description: '',
    quantity: 1,
    uom: 'EA',
    unitPrice: 0,
    taxCode: 'GST10',
    taxAmount: 0,
    glCode: '',
    lineTotal: 0,
    lineTotalIncTax: 0
  }]);
  const [files, setFiles] = useState(rcti?.attachments || []);
  const [errors, setErrors] = useState({});
  const [isDeclarationExpanded, setIsDeclarationExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Get all OrgUnits from shared data
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Sample data for dropdowns
  const vendors = [{
    id: 'v1',
    name: 'ABC Construction',
    allowsRCTI: true,
    taxIdentifier: '123456789'
  }, {
    id: 'v2',
    name: 'XYZ Services',
    allowsRCTI: true,
    taxIdentifier: '987654321'
  }, {
    id: 'v3',
    name: 'Global Contractors',
    allowsRCTI: true,
    taxIdentifier: '456789123'
  }, {
    id: 'v4',
    name: 'Metro Supplies',
    allowsRCTI: false,
    taxIdentifier: '789123456'
  }];
  const currencies = [{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }];
  const taxSchemes = [{
    code: 'GST',
    name: 'Goods and Services Tax'
  }, {
    code: 'VAT',
    name: 'Value Added Tax'
  }, {
    code: 'NONE',
    name: 'No Tax Scheme'
  }];
  const taxCodes = [{
    code: 'GST10',
    name: 'GST 10%',
    rate: 0.1
  }, {
    code: 'VAT20',
    name: 'VAT 20%',
    rate: 0.2
  }, {
    code: 'EXEMPT',
    name: 'Tax Exempt',
    rate: 0
  }];
  const uomOptions = ['EA', 'KG', 'LB', 'M', 'CM', 'L', 'ML', 'HR', 'DAY'];
  // Filter vendors that allow RCTIs
  const filteredVendors = vendors.filter(vendor => vendor.allowsRCTI);
  // Update tax identifier when vendor changes
  useEffect(() => {
    if (formData.vendor) {
      const selectedVendor = vendors.find(v => v.id === formData.vendor);
      if (selectedVendor) {
        setFormData(prev => ({
          ...prev,
          taxIdentifier: selectedVendor.taxIdentifier
        }));
      }
    }
  }, [formData.vendor]);
  // Handle form input changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  // Handle OrgUnit selection
  const handleOrgUnitChange = (orgUnitId, orgUnit) => {
    setFormData({
      ...formData,
      orgUnitId,
      orgUnitName: orgUnit?.name || '',
      orgUnitPath: orgUnit?.path || ''
    });
    // Clear error when field is edited
    if (errors.orgUnitId) {
      setErrors({
        ...errors,
        orgUnitId: null
      });
    }
  };
  // Line item functions
  const addLineItem = () => {
    const newId = lineItems.length > 0 ? Math.max(...lineItems.map(item => item.id)) + 1 : 1;
    setLineItems([...lineItems, {
      id: newId,
      description: '',
      quantity: 1,
      uom: 'EA',
      unitPrice: 0,
      taxCode: 'GST10',
      taxAmount: 0,
      glCode: '',
      lineTotal: 0,
      lineTotalIncTax: 0
    }]);
  };
  const removeLineItem = id => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };
  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        // Recalculate totals if quantity, unit price, or tax code changes
        if (field === 'quantity' || field === 'unitPrice' || field === 'taxCode') {
          const quantity = field === 'quantity' ? value : item.quantity;
          const unitPrice = field === 'unitPrice' ? value : item.unitPrice;
          const taxCode = field === 'taxCode' ? value : item.taxCode;
          // Find tax rate for the selected tax code
          const taxRate = taxCodes.find(tc => tc.code === taxCode)?.rate || 0;
          // Calculate line totals
          const lineTotal = quantity * unitPrice;
          const taxAmount = parseFloat((lineTotal * taxRate).toFixed(2));
          const lineTotalIncTax = lineTotal + taxAmount;
          return {
            ...updatedItem,
            lineTotal,
            taxAmount,
            lineTotalIncTax
          };
        } else if (field === 'taxAmount') {
          // If tax amount is manually updated
          return {
            ...updatedItem,
            lineTotalIncTax: item.lineTotal + value
          };
        }
        return updatedItem;
      }
      return item;
    }));
  };
  // File handling
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  // Calculation functions
  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + item.lineTotal, 0);
  };
  const calculateTotalTax = () => {
    return lineItems.reduce((sum, item) => sum + item.taxAmount, 0);
  };
  const calculateGrandTotal = () => {
    return lineItems.reduce((sum, item) => sum + item.lineTotalIncTax, 0);
  };
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    // Validate required fields
    if (!formData.orgUnitId) newErrors.orgUnitId = 'Organisation unit is required';
    if (!formData.vendor) newErrors.vendor = 'Vendor is required';
    if (!formData.rctiDate) newErrors.rctiDate = 'RCTI date is required';
    if (!formData.currency) newErrors.currency = 'Currency is required';
    if (!formData.taxIdentifier) newErrors.taxIdentifier = 'Tax identifier is required';
    // Validate line items
    const itemErrors = [];
    lineItems.forEach((item, index) => {
      const itemError = {};
      if (!item.description) itemError.description = 'Description is required';
      if (item.quantity <= 0) itemError.quantity = 'Quantity must be greater than 0';
      if (item.unitPrice < 0) itemError.unitPrice = 'Unit price cannot be negative';
      if (!item.taxCode) itemError.taxCode = 'Tax code is required';
      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });
    if (itemErrors.length > 0) {
      newErrors.lineItems = itemErrors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Form submission
  const handleSubmit = (asDraft = true) => {
    // Only validate if not a draft
    if (!asDraft && !validateForm()) {
      return;
    }
    // Prepare data for saving
    const rctiData = {
      ...formData,
      lineItems,
      attachments: files,
      subtotal: calculateSubtotal(),
      totalTax: calculateTotalTax(),
      grandTotal: calculateGrandTotal(),
      status: asDraft ? 'Draft' : 'Sent'
    };
    // Call the save handler from parent
    onSave(rctiData, asDraft);
  };
  return <div className="space-y-6" data-id="element-1112">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1113">
        <div data-id="element-1114">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-1115">
            {rcti ? 'Edit RCTI' : 'Create Recipient Created Tax Invoice'}
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1116">
            {rcti ? `Editing RCTI ${rcti.rctiNumber}` : 'Create a new RCTI on behalf of the vendor'}
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-1117">
          {rcti && <>
              <button onClick={() => onGeneratePDF(rcti)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1118">
                <DownloadIcon className="h-4 w-4 mr-1" data-id="element-1119" />
                Generate PDF
              </button>
              <button onClick={() => onSendEmail(rcti)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1120">
                <MailIcon className="h-4 w-4 mr-1" data-id="element-1121" />
                Email to Vendor
              </button>
            </>}
          <button onClick={() => setShowPreview(!showPreview)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1122">
            <FileTextIcon className="h-4 w-4 mr-1" data-id="element-1123" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${showPreview ? 'lg:grid-cols-2 gap-6' : ''}`} data-id="element-1124">
        {/* RCTI Form */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1125">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-1126">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1127">
              RCTI Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1128">
              Enter all required information for the RCTI
            </p>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-1129">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-1130">
              {/* RCTI Number */}
              <div className="sm:col-span-2" data-id="element-1131">
                <label htmlFor="rctiNumber" className="block text-sm font-medium text-gray-700" data-id="element-1132">
                  RCTI Number
                </label>
                <div className="mt-1" data-id="element-1133">
                  <input type="text" name="rctiNumber" id="rctiNumber" value={formData.rctiNumber} disabled className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border bg-gray-50" data-id="element-1134" />
                </div>
              </div>

              {/* Organisation Unit */}
              <div className="sm:col-span-4" data-id="element-1135">
                <label htmlFor="orgUnit" className="block text-sm font-medium text-gray-700" data-id="element-1136">
                  Organisation Unit <span className="text-red-500" data-id="element-1137">*</span>
                </label>
                <div className="mt-1" data-id="element-1138">
                  <OrgUnitSelector value={formData.orgUnitId} onChange={handleOrgUnitChange} label="" showPath={true} error={errors.orgUnitId} data-id="element-1139" />
                  {errors.orgUnitId && <p className="mt-1 text-sm text-red-600" data-id="element-1140">
                      {errors.orgUnitId}
                    </p>}
                </div>
              </div>

              {/* Vendor */}
              <div className="sm:col-span-3" data-id="element-1141">
                <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-1142">
                  Vendor <span className="text-red-500" data-id="element-1143">*</span>
                </label>
                <div className="mt-1" data-id="element-1144">
                  <select id="vendor" name="vendor" value={formData.vendor} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.vendor ? 'border-red-300' : ''}`} data-id="element-1145">
                    <option value="" data-id="element-1146">Select vendor</option>
                    {filteredVendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-1147">
                        {vendor.name}
                      </option>)}
                  </select>
                  {errors.vendor && <p className="mt-1 text-sm text-red-600" data-id="element-1148">{errors.vendor}</p>}
                </div>
                {filteredVendors.length === 0 && <p className="mt-1 text-sm text-amber-600" data-id="element-1149">
                    No vendors with RCTI enabled are available.
                  </p>}
              </div>

              {/* RCTI Date */}
              <div className="sm:col-span-3" data-id="element-1150">
                <label htmlFor="rctiDate" className="block text-sm font-medium text-gray-700" data-id="element-1151">
                  RCTI Date <span className="text-red-500" data-id="element-1152">*</span>
                </label>
                <div className="mt-1" data-id="element-1153">
                  <input type="date" name="rctiDate" id="rctiDate" value={formData.rctiDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.rctiDate ? 'border-red-300' : ''}`} data-id="element-1154" />
                  {errors.rctiDate && <p className="mt-1 text-sm text-red-600" data-id="element-1155">
                      {errors.rctiDate}
                    </p>}
                </div>
              </div>

              {/* Currency */}
              <div className="sm:col-span-2" data-id="element-1156">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-1157">
                  Currency <span className="text-red-500" data-id="element-1158">*</span>
                </label>
                <div className="mt-1" data-id="element-1159">
                  <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.currency ? 'border-red-300' : ''}`} data-id="element-1160">
                    <option value="" data-id="element-1161">Select currency</option>
                    {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-1162">
                        {currency.code} - {currency.name}
                      </option>)}
                  </select>
                  {errors.currency && <p className="mt-1 text-sm text-red-600" data-id="element-1163">
                      {errors.currency}
                    </p>}
                </div>
              </div>

              {/* Tax Scheme */}
              <div className="sm:col-span-2" data-id="element-1164">
                <label htmlFor="taxScheme" className="block text-sm font-medium text-gray-700" data-id="element-1165">
                  Tax Scheme <span className="text-red-500" data-id="element-1166">*</span>
                </label>
                <div className="mt-1" data-id="element-1167">
                  <select id="taxScheme" name="taxScheme" value={formData.taxScheme} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.taxScheme ? 'border-red-300' : ''}`} data-id="element-1168">
                    <option value="" data-id="element-1169">Select tax scheme</option>
                    {taxSchemes.map(scheme => <option key={scheme.code} value={scheme.code} data-id="element-1170">
                        {scheme.name}
                      </option>)}
                  </select>
                  {errors.taxScheme && <p className="mt-1 text-sm text-red-600" data-id="element-1171">
                      {errors.taxScheme}
                    </p>}
                </div>
              </div>

              {/* Tax Identifier */}
              <div className="sm:col-span-2" data-id="element-1172">
                <label htmlFor="taxIdentifier" className="block text-sm font-medium text-gray-700" data-id="element-1173">
                  Vendor Tax Identifier <span className="text-red-500" data-id="element-1174">*</span>
                </label>
                <div className="mt-1" data-id="element-1175">
                  <input type="text" name="taxIdentifier" id="taxIdentifier" value={formData.taxIdentifier} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.taxIdentifier ? 'border-red-300' : ''}`} placeholder="Enter tax identifier" data-id="element-1176" />
                  {errors.taxIdentifier && <p className="mt-1 text-sm text-red-600" data-id="element-1177">
                      {errors.taxIdentifier}
                    </p>}
                </div>
              </div>

              {/* Reference */}
              <div className="sm:col-span-6" data-id="element-1178">
                <label htmlFor="reference" className="block text-sm font-medium text-gray-700" data-id="element-1179">
                  Reference / Contract
                </label>
                <div className="mt-1" data-id="element-1180">
                  <input type="text" name="reference" id="reference" value={formData.reference} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter reference or contract number" data-id="element-1181" />
                </div>
              </div>

              {/* Notes */}
              <div className="sm:col-span-6" data-id="element-1182">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700" data-id="element-1183">
                  Notes
                </label>
                <div className="mt-1" data-id="element-1184">
                  <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter additional notes" data-id="element-1185" />
                </div>
              </div>

              {/* Line Items */}
              <div className="sm:col-span-6" data-id="element-1186">
                <div className="flex justify-between items-center mb-4" data-id="element-1187">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1188">
                    Line Items
                  </h3>
                </div>
                {lineItems.map((item, index) => <div key={item.id} className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-1189">
                    <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-1190">
                      <h4 className="text-base font-medium text-gray-900" data-id="element-1191">
                        Item {index + 1}
                      </h4>
                      <button type="button" onClick={() => removeLineItem(item.id)} className="text-red-600 hover:text-red-900" disabled={lineItems.length === 1} data-id="element-1192">
                        <TrashIcon className="h-5 w-5" data-id="element-1193" />
                      </button>
                    </div>
                    <div className="p-4" data-id="element-1194">
                      <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6" data-id="element-1195">
                        {/* Description */}
                        <div className="sm:col-span-6" data-id="element-1196">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1197">
                            Description <span className="text-red-500" data-id="element-1198">*</span>
                          </label>
                          <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.description ? 'border-red-300' : ''}`} value={item.description} onChange={e => updateLineItem(item.id, 'description', e.target.value)} placeholder="Enter description" data-id="element-1199" />
                          {errors.lineItems && errors.lineItems[index]?.description && <p className="mt-1 text-sm text-red-600" data-id="element-1200">
                                {errors.lineItems[index].description}
                              </p>}
                        </div>

                        {/* Quantity */}
                        <div className="sm:col-span-1" data-id="element-1201">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1202">
                            Quantity <span className="text-red-500" data-id="element-1203">*</span>
                          </label>
                          <input type="number" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.quantity ? 'border-red-300' : ''}`} value={item.quantity} onChange={e => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)} min="0.01" step="0.01" data-id="element-1204" />
                          {errors.lineItems && errors.lineItems[index]?.quantity && <p className="mt-1 text-sm text-red-600" data-id="element-1205">
                                {errors.lineItems[index].quantity}
                              </p>}
                        </div>

                        {/* UOM */}
                        <div className="sm:col-span-1" data-id="element-1206">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1207">
                            Unit of Measure
                          </label>
                          <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.uom} onChange={e => updateLineItem(item.id, 'uom', e.target.value)} data-id="element-1208">
                            {uomOptions.map(uom => <option key={uom} value={uom} data-id="element-1209">
                                {uom}
                              </option>)}
                          </select>
                        </div>

                        {/* Unit Price */}
                        <div className="sm:col-span-1" data-id="element-1210">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1211">
                            Unit Price <span className="text-red-500" data-id="element-1212">*</span>
                          </label>
                          <input type="number" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.unitPrice ? 'border-red-300' : ''}`} value={item.unitPrice} onChange={e => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" data-id="element-1213" />
                          {errors.lineItems && errors.lineItems[index]?.unitPrice && <p className="mt-1 text-sm text-red-600" data-id="element-1214">
                                {errors.lineItems[index].unitPrice}
                              </p>}
                        </div>

                        {/* Tax Code */}
                        <div className="sm:col-span-1" data-id="element-1215">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1216">
                            Tax Code <span className="text-red-500" data-id="element-1217">*</span>
                          </label>
                          <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.taxCode ? 'border-red-300' : ''}`} value={item.taxCode} onChange={e => updateLineItem(item.id, 'taxCode', e.target.value)} data-id="element-1218">
                            <option value="" data-id="element-1219">Select tax code</option>
                            {taxCodes.map(code => <option key={code.code} value={code.code} data-id="element-1220">
                                {code.code} - {code.name}
                              </option>)}
                          </select>
                          {errors.lineItems && errors.lineItems[index]?.taxCode && <p className="mt-1 text-sm text-red-600" data-id="element-1221">
                                {errors.lineItems[index].taxCode}
                              </p>}
                        </div>

                        {/* Line Total */}
                        <div className="sm:col-span-1" data-id="element-1222">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1223">
                            Line Total
                          </label>
                          <div className="p-2 bg-gray-50 border border-gray-200 rounded-md text-right" data-id="element-1224">
                            {formatCurrency(item.lineTotal, formData.currency, locale)}
                          </div>
                        </div>

                        {/* Tax Amount */}
                        <div className="sm:col-span-1" data-id="element-1225">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1226">
                            Tax Amount
                          </label>
                          <div className="p-2 bg-gray-50 border border-gray-200 rounded-md text-right" data-id="element-1227">
                            {formatCurrency(item.taxAmount, formData.currency, locale)}
                          </div>
                        </div>

                        {/* GL Code - Optional */}
                        <div className="sm:col-span-2" data-id="element-1228">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1229">
                            GL Code
                          </label>
                          <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.glCode} onChange={e => updateLineItem(item.id, 'glCode', e.target.value)} placeholder="Enter GL code" data-id="element-1230" />
                        </div>

                        {/* Total Inc Tax */}
                        <div className="sm:col-span-2" data-id="element-1231">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1232">
                            Total (Inc. Tax)
                          </label>
                          <div className="p-2 bg-gray-50 border border-gray-200 rounded-md text-right font-medium" data-id="element-1233">
                            {formatCurrency(item.lineTotalIncTax, formData.currency, locale)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
                <div className="mt-2" data-id="element-1234">
                  <button type="button" onClick={addLineItem} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1235">
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-1236" />
                    Add Line Item
                  </button>
                </div>

                {/* Summary Totals */}
                <div className="mt-6 flex justify-end" data-id="element-1237">
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-300 w-64" data-id="element-1238">
                    <div className="flex justify-between items-center text-sm mb-2" data-id="element-1239">
                      <span data-id="element-1240">Subtotal:</span>
                      <span data-id="element-1241">
                        {formatCurrency(calculateSubtotal(), formData.currency, locale)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm mb-2" data-id="element-1242">
                      <span data-id="element-1243">Total Tax:</span>
                      <span data-id="element-1244">
                        {formatCurrency(calculateTotalTax(), formData.currency, locale)}
                      </span>
                    </div>
                    <div className="border-t border-gray-300 my-2" data-id="element-1245"></div>
                    <div className="flex justify-between items-center text-lg font-medium" data-id="element-1246">
                      <span data-id="element-1247">Grand Total:</span>
                      <span data-id="element-1248">
                        {formatCurrency(calculateGrandTotal(), formData.currency, locale)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* RCTI Declaration */}
              <div className="sm:col-span-6" data-id="element-1249">
                <div className="flex justify-between items-center mb-2 cursor-pointer" onClick={() => setIsDeclarationExpanded(!isDeclarationExpanded)} data-id="element-1250">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1251">
                    RCTI Declaration
                  </h3>
                  <button type="button" className="text-gray-400 hover:text-gray-500" data-id="element-1252">
                    {isDeclarationExpanded ? <ChevronUpIcon className="h-5 w-5" data-id="element-1253" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-1254" />}
                  </button>
                </div>
                {isDeclarationExpanded && <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4" data-id="element-1255">
                    <label htmlFor="declarationText" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1256">
                      Declaration Text
                    </label>
                    <textarea id="declarationText" name="declarationText" rows={4} value={formData.declarationText} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-1257" />
                    <p className="mt-2 text-xs text-gray-500 flex items-start" data-id="element-1258">
                      <InfoIcon className="h-4 w-4 text-blue-500 mr-1 flex-shrink-0 mt-0.5" data-id="element-1259" />
                      This declaration will appear on the PDF sent to the
                      vendor. It should comply with tax regulations for RCTIs in
                      your jurisdiction.
                    </p>
                  </div>}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-1260">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1261">
                    Preview of Declaration
                  </h4>
                  <div className="text-sm text-gray-600 italic" data-id="element-1262">
                    {formData.declarationText}
                  </div>
                </div>
              </div>

              {/* Supporting Documents */}
              <div className="sm:col-span-6" data-id="element-1263">
                <div className="flex justify-between items-center mb-4" data-id="element-1264">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1265">
                    Supporting Documents
                  </h3>
                </div>
                <FileUpload onFileUpload={handleFileUpload} data-id="element-1266" />
                {files.length > 0 && <div className="mt-4" data-id="element-1267">
                    <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1268">
                      Uploaded Files
                    </h4>
                    <ul className="border rounded-md divide-y divide-gray-200" data-id="element-1269">
                      {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-1270">
                          <div className="flex items-center" data-id="element-1271">
                            <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-1272" />
                            <span className="ml-2 flex-1 w-0 truncate" data-id="element-1273">
                              {file.name}
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-1274">
                            <span className="text-xs text-gray-500 mr-4" data-id="element-1275">
                              {(file.size / 1024).toFixed(1)} KB
                            </span>
                            <button type="button" onClick={() => removeFile(index)} className="text-red-600 hover:text-red-900" data-id="element-1276">
                              <TrashIcon className="h-4 w-4" data-id="element-1277" />
                            </button>
                          </div>
                        </li>)}
                    </ul>
                  </div>}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-3" data-id="element-1278">
            <button type="button" onClick={onCancel} className="inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1279">
              <XIcon className="h-4 w-4 mr-1" data-id="element-1280" />
              Cancel
            </button>
            <button type="button" onClick={() => handleSubmit(true)} className="inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1281">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-1282" />
              Save as Draft
            </button>
            <button type="button" onClick={() => handleSubmit(false)} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1283">
              <MailIcon className="h-4 w-4 mr-1" data-id="element-1284" />
              Generate & Send
            </button>
          </div>
        </div>

        {/* RCTI Preview */}
        {showPreview && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1285">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-1286">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1287">
                RCTI Preview
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1288">
                Preview of how the RCTI will appear when generated
              </p>
            </div>
            <div className="px-4 py-5 sm:p-6" data-id="element-1289">
              <div className="border border-gray-200 rounded-lg p-6" data-id="element-1290">
                {/* Header with logo and title */}
                <div className="flex justify-between items-start mb-8" data-id="element-1291">
                  <div data-id="element-1292">
                    <img src="https://via.placeholder.com/200x50?text=Company+Logo" alt="Company Logo" className="h-12 mb-2" data-id="element-1293" />
                    <div className="text-sm text-gray-500" data-id="element-1294">
                      {formData.orgUnitName || 'Organisation Unit'}
                      <br data-id="element-1295" />
                      {formData.orgUnitPath && <>
                          {formData.orgUnitPath}
                          <br data-id="element-1296" />
                        </>}
                      123 Business Street
                      <br data-id="element-1297" />
                      Business City, State 12345
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1298">
                    <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1299">
                      RECIPIENT CREATED TAX INVOICE
                    </h2>
                    <div className="text-sm" data-id="element-1300">
                      <div className="mb-1" data-id="element-1301">
                        <span className="font-medium" data-id="element-1302">RCTI Number:</span>{' '}
                        {formData.rctiNumber}
                      </div>
                      <div className="mb-1" data-id="element-1303">
                        <span className="font-medium" data-id="element-1304">Date:</span>{' '}
                        {formData.rctiDate}
                      </div>
                      <div className="mb-1" data-id="element-1305">
                        <span className="font-medium" data-id="element-1306">Reference:</span>{' '}
                        {formData.reference || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vendor details */}
                <div className="mb-8" data-id="element-1307">
                  <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1308">
                    Vendor:
                  </h3>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-1309">
                    <div className="text-lg font-medium" data-id="element-1310">
                      {vendors.find(v => v.id === formData.vendor)?.name || 'Vendor Name'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1" data-id="element-1311">
                      Vendor Address Line 1<br data-id="element-1312" />
                      Vendor Address Line 2<br data-id="element-1313" />
                      Vendor City, State 54321
                    </div>
                    <div className="text-sm mt-2" data-id="element-1314">
                      <span className="font-medium" data-id="element-1315">Tax Identifier:</span>{' '}
                      {formData.taxIdentifier}
                    </div>
                  </div>
                </div>

                {/* Line items */}
                <div className="mb-8" data-id="element-1316">
                  <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1317">
                    Line Items:
                  </h3>
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-1318">
                    <thead className="bg-gray-50" data-id="element-1319">
                      <tr data-id="element-1320">
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1321">
                          Description
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1322">
                          Quantity
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1323">
                          Unit Price
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1324">
                          Tax Code
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1325">
                          Tax Amount
                        </th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1326">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-1327">
                      {lineItems.map(item => <tr key={item.id} data-id="element-1328">
                          <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1329">
                            {item.description || 'Item description'}
                            {item.glCode && <div className="text-xs text-gray-500" data-id="element-1330">
                                GL: {item.glCode}
                              </div>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1331">
                            {item.quantity} {item.uom}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1332">
                            {formatCurrency(item.unitPrice, formData.currency, locale)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1333">
                            {item.taxCode}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1334">
                            {formatCurrency(item.taxAmount, formData.currency, locale)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium" data-id="element-1335">
                            {formatCurrency(item.lineTotalIncTax, formData.currency, locale)}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end mb-8" data-id="element-1336">
                  <div className="w-64" data-id="element-1337">
                    <div className="flex justify-between py-2 text-sm" data-id="element-1338">
                      <span data-id="element-1339">Subtotal:</span>
                      <span data-id="element-1340">
                        {formatCurrency(calculateSubtotal(), formData.currency, locale)}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 text-sm border-b border-gray-200" data-id="element-1341">
                      <span data-id="element-1342">Total Tax:</span>
                      <span data-id="element-1343">
                        {formatCurrency(calculateTotalTax(), formData.currency, locale)}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 text-base font-bold" data-id="element-1344">
                      <span data-id="element-1345">Grand Total:</span>
                      <span data-id="element-1346">
                        {formatCurrency(calculateGrandTotal(), formData.currency, locale)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Declaration */}
                <div className="border-t border-gray-200 pt-6 text-sm text-gray-600" data-id="element-1347">
                  <h3 className="text-base font-medium text-gray-900 mb-2" data-id="element-1348">
                    Declaration:
                  </h3>
                  <p className="italic" data-id="element-1349">{formData.declarationText}</p>
                </div>

                {/* Notes */}
                {formData.notes && <div className="mt-6 text-sm text-gray-600" data-id="element-1350">
                    <h3 className="text-base font-medium text-gray-900 mb-2" data-id="element-1351">
                      Notes:
                    </h3>
                    <p data-id="element-1352">{formData.notes}</p>
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}